import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ScopeUpload from '../../components/mentor/ScopeUpload/ScopeUpload';
import ScopeViewer from '../../components/common/ScopeViewer/ScopeViewer';
import { getMyProjects, submitProjectScope, getProjectMilestones, markMilestoneComplete } from '../../services/projectService';
import { formatDate } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import styles from './MentorProjects.module.css';

const STATUS_HEADINGS = {
  in_progress: 'In Progress Projects',
  not_started: 'Not Started Projects',
  completed: 'Completed Projects',
};

// `status` is set by the sidebar subtabs; without it the page lists every project (incl. on_hold).
const MentorProjects = ({ status }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getMyProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProject = (project) => {
    navigate(`/mentor/projects/${project.id}`);
  };

  const handleSubmitScope = async (scopeJson, fileMeta) => {
    try {
      await submitProjectScope(selectedProject.id, scopeJson, fileMeta);
      setShowScopeModal(false);
      fetchProjects();
      toast.success('Project scope submitted for approval');
    } catch (error) {
      toast.error('Error submitting scope: ' + error.message);
    }
  };

  const handleMarkComplete = async (milestoneId) => {
    try {
      await markMilestoneComplete(milestoneId);
      const milestonesData = await getProjectMilestones(selectedProject.id);
      setMilestones(milestonesData);
      toast.success('Milestone marked as complete. Awaiting admin approval.');
    } catch (error) {
      toast.error('Error marking milestone: ' + error.message);
    }
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  // The main tab hides completed projects (they live in the Completed subtab).
  // Newest created first; projects without a created_at go last.
  const visibleProjects = projects
    .filter(p => (status ? p.status === status : p.status !== 'completed'))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return (
    <div className={styles.projectsPage}>
      <h1>{STATUS_HEADINGS[status] || 'My Projects'}</h1>

      {!selectedProject ? (
        <div className={styles.projectsGrid}>
          {visibleProjects.length === 0 ? (
            <Card>
              <p className={styles.empty}>{status ? 'No projects in this status' : 'No projects assigned yet'}</p>
            </Card>
          ) : (
            visibleProjects.map(project => (
              <Card key={project.id} hover className={styles.projectCard}>
                <div className={styles.projectHeader}>
                  <h3>{project.student_name ? `${project.student_name} - ${project.project_name}` : project.project_name}</h3>
                  <StatusBadge status={project.status} />
                </div>
                <div className={styles.projectDetails}>
                  <p><strong>Track:</strong> {project.project_track.replace(/_/g, ' ').toUpperCase()}</p>
                  <p><strong>Start Date:</strong> {formatDate(project.start_date)}</p>
                  <p><strong>End Date:</strong> {formatDate(project.end_date)}</p>
                  {project.meet_link && (
                    <a href={project.meet_link} target="_blank" rel="noopener noreferrer" className={styles.meetLink}>
                       Join Google Meet
                    </a>
                  )}
                </div>
                <div className={styles.projectActions}>
                  <Button size="small" onClick={() => handleViewProject(project)}>
                    View Details
                  </Button>
                  {!project.project_scope && (
                    <Button size="small" variant="secondary" onClick={() => { setSelectedProject(project); setShowScopeModal(true); }}>
                      Upload Scope
                    </Button>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      ) : (
        <div>
          <Button variant="outline" onClick={() => setSelectedProject(null)} className={styles.backBtn}>
            ← Back to Projects
          </Button>

          <Card title={selectedProject.project_name}>
            <div className={styles.projectInfo}>
              <p><strong>Description:</strong> {selectedProject.project_description || 'N/A'}</p>
              <p><strong>Track:</strong> {selectedProject.project_track.replace(/_/g, ' ').toUpperCase()}</p>
              <p><strong>Status:</strong> <StatusBadge status={selectedProject.status} /></p>
              {selectedProject.project_scope && (
                <div className={styles.scopeSection}>
                  <strong>Project Scope:</strong>
                  <ScopeViewer
                    scopeData={selectedProject.project_scope}
                    projectId={selectedProject.id}
                    fileName={selectedProject.scope_file_name}
                  />
                  <span className={styles.scopeStatus}>
                    {selectedProject.scope_approved ? 'Approved' : 'Pending Approval'}
                  </span>
                </div>
              )}
            </div>
          </Card>

          <Card title="Milestones">
            <div className={styles.milestonesList}>
              {milestones.map(milestone => (
                <div key={milestone.id} className={styles.milestoneItem}>
                  <div className={styles.milestoneContent}>
                    <h4>{milestone.milestone_name}</h4>
                    <ul className={styles.milestoneItems}>
                      {milestone.milestone_items?.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                    <StatusBadge status={milestone.status} />
                  </div>
                  {milestone.status === 'pending' && (
                    <Button size="small" onClick={() => handleMarkComplete(milestone.id)}>
                      Mark Complete
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <Modal isOpen={showScopeModal} onClose={() => setShowScopeModal(false)} title="Upload Project Scope" wide>
        <ScopeUpload
          projectId={selectedProject?.id}
          onSubmit={handleSubmitScope}
          onCancel={() => setShowScopeModal(false)}
        />
      </Modal>
    </div>
  );
};

export default MentorProjects;